import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsList from "@/components/CardsList";
import EmptyData from "@/components/EmptyData";
import HandleMedicalRecordModal from "@/pages/Medical/Modals/handleRecord";

import {MedicalAPI, loadMedicalRosterRecords} from "@/api/medical";

import AddIcon from "@mui/icons-material/Add";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {playerToOption} from "@/utils/functions";
import {usePlayers} from "@/utils/hooks";

function MedicalRecordsPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(true);
	const [records, setRecords] = useState([]);

	const [openModal, setOpenModal] = useState(false);

	const {players, loading: playersLoading} = usePlayers({
		setLoading: false,
		handleNotification,
		goalkeepers: false,
		onlyInternal: true,
		prospect: false,
	});

	useEffect(() => {
		let active = true;
		setLoading(true);
		setRecords([]);
		loadMedicalRosterRecords(seasonId)
			.then((data) => {
				if (active) setRecords(data);
			})
			.catch(() => {
				if (active) handleNotification(ERROR_OPERATION, "error");
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [seasonId]);

	const saveRecord = async (data) => {
		setLoading(true);
		setOpenModal(false);
		try {
			// Se il giocatore ha già una cartella (anche fuori dalle rose della stagione) si apre quella
			const playerId = data?.player?.value;
			const existing = (await MedicalAPI.recordsList({playerId}))?.find(
				(r) => r.playerId === playerId,
			);
			if (existing) {
				navigate("/medical/records/" + existing.id);
				return;
			}
			const response = await MedicalAPI.recordCreate({playerId, notes: ""});
			navigate("/medical/records/" + response?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const groupedRecords = {};
	(records || []).forEach((r) => {
		for (const roster of r.currentRosters || []) {
			(groupedRecords[roster.name] = groupedRecords[roster.name] || []).push({
				...r,
				playerLastName: r.playerData?.lastName,
			});
		}
	});

	const getListConfiguration = () => {
		return {
			data: groupedRecords,
			onClick: (record) => navigate("/medical/records/" + record.id),
			imageUrl: (item) => require("@/assets/images/medical/record.png"),
			title: (record) =>
				record?.playerData
					? record?.playerData?.firstName + " " + record?.playerData?.lastName
					: "---",
			subtitle: (record) =>
				record?.playerData
					? moment(record?.playerData.dateOfBirth).format("YYYY")
					: "---",
		};
	};

	const mainActions = [
		{
			icon: <AddIcon />,
			title: "Nuova cartella",
			onClick: () => {
				setOpenModal(true);
			},
		},
	];

	const recordsPlayerIds = records?.map((r) => r.playerId) || [];

	return (
		<Page>
			<Loading visible={loading || playersLoading} />
			<Breadcrumbs
				current="Cartelle"
				actions={<MainActions actions={mainActions} />}
			/>
			{!loading && !records.length && (
				<EmptyData>
					Nessuna cartella per i giocatori delle rose di questa stagione.
				</EmptyData>
			)}
			<CardsList
				grouped
				groupSorted={false}
				type="medical_record"
				listConfiguration={getListConfiguration()}
				defaultSortedField="playerLastName"
				defaultSortedOrder="ascend"
			/>
			{openModal ? (
				<HandleMedicalRecordModal
					open={openModal}
					confirm={saveRecord}
					close={() => setOpenModal(false)}
					players={players
						?.filter((p) => recordsPlayerIds.indexOf(p.id) === -1)
						?.map((p) => playerToOption(p))}
				/>
			) : null}
		</Page>
	);
}

export default MedicalRecordsPage;
